
import { PRODUCT_DETAIL_FIELDS } from '../constant/page-constant';

/**
 * Extract size from product name if it contains a trailing parenthesized value.
 * e.g. "A Fenac (3 ml)" → { cleanName: "A Fenac", size: "3 ml" }
 *      "A Fenac"         → { cleanName: "A Fenac", size: "" }
 */
export const extractSizeFromName = (name) => {
  const raw = String(name || '').trim()
  const match = raw.match(/\(([^)]+)\)\s*$/)
  if (match) {
    return { cleanName: raw.slice(0, match.index).trim(), size: match[1].trim() }
  }
  return { cleanName: raw, size: '' }
}

/**
 * Resolve the effective strength: use `strength` if available, otherwise fall back to `dose`.
 */
export const resolveStrength = (product) => {
  return String(product.strength || product.dose || '').trim()
}

/**
 * Resolve the effective size: use `size` field if available, otherwise extract from name brackets.
 */
export const resolveSize = (product) => {
  const sizeField = product.size != null ? String(product.size).trim() : ''
  if (sizeField) return sizeField

  const rawName = String(
    product.name ?? product.product_name ?? product.label ?? product.productName ?? ''
  ).trim()
  return extractSizeFromName(rawName).size
}

/**
 * Product list / dropdown display: CleanName [ (size) ] [ -strength ] [ -category ]
 * - Size: from dedicated `size` field, or extracted from name brackets if empty.
 * - Strength: from `strength` field, falls back to `dose`.
 * - receivingTableRow: Product Receiving line items.
 */
export const formatProductListLabel = (product, options = {}) => {
  if (!product) return ''

  const includeCategory = options.includeCategory !== false
  const receivingTableRow = options.receivingTableRow === true

  const name = String(
    receivingTableRow
      ? (product.productName ?? product.name ?? '')
      : (product.name ?? product.product_name ?? product.label ?? product.productName ?? '')
  ).trim()

  const size = receivingTableRow
    ? (product.sizeOrMedType === 'Size' && product.sizeOrMedValue
        ? String(product.sizeOrMedValue).trim()
        : '')
    : String(product.size ?? '').trim()

  // receivingTableRow items don't fall back to dose
  const potency = String(
    receivingTableRow ? (product.strength ?? '') : (product.strength ?? product.dose ?? '')
  ).trim()

  let category = ''
  if (!receivingTableRow && includeCategory && product.category != null) {
    category = typeof product.category === 'string'
      ? product.category.trim()
      : String(product.category.label || product.category.value || '').trim()
  }

  // Name (size)-strength-category  /  Name-strength-category  /  Name (size)-strength  /  Name-strength
  let out = name
  if (size) out += ` (${size})`
  if (potency) out += `-${potency}`
  if (category) out += `-${category}`
  return out
}

export const sortBatchesByExpiry = (inventory = []) => {
  return [...inventory].sort((a, b) => {
    const dateA = new Date(a[PRODUCT_DETAIL_FIELDS.EXPIRY_DATE])
    const dateB = new Date(b[PRODUCT_DETAIL_FIELDS.EXPIRY_DATE])
    return dateA - dateB
  })
}


export const calculateFEFOBatches = (inventory = [], requiredQuantity = 0) => {
  if (!inventory.length || !requiredQuantity) return []

  const sortedBatches = sortBatchesByExpiry(inventory)
  const allocatedBatches = []
  let remainingQuantity = parseInt(requiredQuantity)

  for (const batch of sortedBatches) {
    if (remainingQuantity <= 0) break

    const availableStock = parseInt(batch[PRODUCT_DETAIL_FIELDS.STOCK])
    const allocatedQty = Math.min(remainingQuantity, availableStock)

    allocatedBatches.push({
      ...batch,
      allocatedQuantity: allocatedQty,
    })

    remainingQuantity -= allocatedQty
  }

  return allocatedBatches
}

// ✅ Sum all batch stock
export const getTotalAvailableStock = (inventory = []) => {
  return inventory.reduce(
    (sum, batch) => sum + parseInt(batch[PRODUCT_DETAIL_FIELDS.STOCK] || 0),
    0
  )
}
